import { Deferred, deferred } from "client/Misc/utils";
import {
	ReactElement,
	useCallback,
	useState
} from "react";

export type OpenDialogFactory<TOpenData> = (
	openData: TOpenData,
	onClose: () => void
) => ReactElement | null;

export function useOpenDialog<TOpenData>(
	dialogFactory: OpenDialogFactory<TOpenData>
): [
	element: ReactElement | null,
	open: (openData: TOpenData) => Promise<void>
] {
	const [state, setState] = useState<State<TOpenData>>({ isOpen: false });

	const openHandler = useCallback(
		(data: TOpenData): Promise<void> => {
			const resultDeferred: Deferred<void> = deferred<void>();

			setState(
				state => {
					if (state.isOpen) {
						state.deferred
							.reject("The new instance of this dialog is open again.");
					}

					return {
						isOpen: true,
						openData: data,
						deferred: resultDeferred
					};
				}
			);

			return resultDeferred.promise;
		},
		[]
	);

	const closeHandler = useCallback(
		(): void => {
			setState(
				state => {
					if (state.isOpen) {
						state.deferred
							.resolve();
					}

					return { isOpen: false };
				}
			);
		},
		[]
	);

	const renderElement = (): ReactElement | null => {
		if (!state.isOpen) {
			return null;
		}

		const {
			openData
		} = state;

		return (
			dialogFactory(
				openData,
				closeHandler
			)
		);
	};

	return [
		renderElement(),
		openHandler
	];
}

type State<TOpenData> =
	| {
		isOpen: false;
	}
	| {
		isOpen: true;
		openData: TOpenData;
		deferred: Deferred<void>;
	};